import { MembershipOffer, type MembershipOfferInfo } from "./MembershipOffer";
import {BRAND} from '../shared/brand.js';
import {parentAuditTab} from './math/audit-navigation';
import {PrizePriceConfirmation} from './PrizePriceConfirmation';
import {notificationParentRoute} from '../shared/notifications/preferences';

import {CertificatesPanel} from './engagement/CertificatesPanel';
import './engagement/engagement.css';
import {
  ArrowClockwise,
  Check,
  CheckCircle,
  ClipboardText,
  Coins,
  Compass,
  EnvelopeSimple,
  Gear,
  DotsThreeOutline,
  Gift,
  Gauge,
  Medal,
  PencilSimple,
  Plus,
  SignOut,
  ShieldCheck,
  SlidersHorizontal,
  Sparkle,
  Star,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { lazy, Suspense, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import {EXPERIENCE_PRIZES,suggestedTierPrice} from '../shared/engagement/rewards';
import {ENGAGEMENT_CONFIG, defaultDailyXpGoalFor} from "../shared/engagement/config";
import { DIFFICULTY_LABELS, starterSetShape, type Difficulty } from "../shared/starterSet";
import { BAND_DEFAULTS, CATEGORY_LABELS, effectiveCost, type Band, type PrizeCategory, unitLabel } from "../shared/economy";
import { GRADES, GRADE_FROM_BAND, bandForGrade, gradeLabel } from "../shared/grades";
import { countdownText } from "../shared/math/test-plan";
import { FamilySetup, GettingStartedCard } from "./onboarding/FamilySetup";
import { api } from "./api";
import "./math/math.css";
import {leaveBrowserQuery} from "../shared/math/content-browser";
import {leaveReviewQuery} from "../shared/math/content-review";
import { GuideTab } from "./GuideTab";
import { SummitRun } from "./SummitRun";
import { BrandLockup, SummitMark } from "./Brand";
import "./parent-club.css";
import { useDialog } from "./useDialog";
import { PrizeArt } from "./prizeArt";
import { StudentDashboard } from "./StudentDashboard";
import { MasteryBonusForm } from "./MasteryBonusForm";
import { masteryBonusSubmission, parentBonusAvailable } from "./masteryBonusSubmission";
import type { OwedPrize, LibraryData, ParentDashboardData, ParentWishRow, Prize, StudentSettings, VerifyEntry } from "./types";

const ParentNotifications=lazy(()=>import("./notifications/ParentNotifications").then(m=>({default:m.ParentNotifications})));
const WeeklyMathReports=lazy(()=>import("./math/WeeklyMathReport").then(m=>({default:m.WeeklyMathReports})));
const ContentBrowser=lazy(()=>import("./math/ContentBrowser"));
const ContentReview=lazy(()=>import("./math/ContentReview"));
const MathParent=lazy(()=>import("./math/MathLearning").then(m=>({default:m.MathParent})));

type Tab = "notifications" | "curriculum" | "review" | "math" | "overview" | "verify" | "crew" | "library" | "guide" | "settings";

export function ParentDashboard({ onLogout }: { onLogout: () => Promise<void> }) {
  const [data, setData] = useState<ParentDashboardData | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") { setMoreOpen(false); moreButtonRef.current?.focus(); } };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [moreOpen]);
  const [tab, setTab] = useState<Tab>(()=>{const admin=new URLSearchParams(window.location.search).get("admin");return admin==="curriculum"?"curriculum":admin==="review"?"review":"overview";});
  function changeTab(next:Tab) {
    setTab(next);
    {let search=window.location.search;if(next!=="curriculum")search="?"+leaveBrowserQuery(search);if(next!=="review")search="?"+leaveReviewQuery(search);search=search.replace(/^\?/,"");window.history.replaceState(null,"",window.location.pathname+(search?"?"+search:"")+window.location.hash);}
  }
  const [mathLearnerId,setMathLearnerId]=useState<string>();
  const [error, setError] = useState("");
  const [students, setStudents] = useState<StudentSettings[]>([]);
  const [studentEditor, setStudentEditor] = useState<StudentSettings | "new" | null>(null);
  const [certificatesFor,setCertificatesFor]=useState<{id:string;name:string}|null>(null);
  const [auditFor, setAuditFor] = useState<{ id: string; name: string } | null>(null);
  const [circulationOpen, setCirculationOpen] = useState(false);
  const [prizeEditor, setPrizeEditor] = useState<Prize | "new" | null>(null);
  const [kidPreview, setKidPreview] = useState<string | null>(null);
  const [tryRun, setTryRun] = useState(false);
  const [masteryFor, setMasteryFor] = useState<(StudentSettings & { send: ReturnType<typeof masteryBonusSubmission> }) | null>(null);

  useEffect(()=>{const apply=()=>{const next=parentAuditTab(window.location.hash);if(next)setTab(next);};apply();window.addEventListener('hashchange',apply);return()=>window.removeEventListener('hashchange',apply);},[]);

  useEffect(()=>{const route=()=>{const next=notificationParentRoute(location.hash);if(next){setTab(next.tab);if(next.studentId)setMathLearnerId(next.studentId);}};route();window.addEventListener('hashchange',route);return()=>window.removeEventListener('hashchange',route);},[]);
  // Summit Run is off until a parent turns it on, per kid.
  async function toggleGame(studentId: string, enabled: boolean) {
    try {
      await api(`/api/parent/students/${studentId}/game`, { method: "POST", body: JSON.stringify({ enabled }) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not change that"); }
  }

  async function fulfil(id: string) {
    try {
      await api(`/api/parent/redemptions/${id}/fulfil`, { method: "POST", body: JSON.stringify({}) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not check that off"); }
  }

  async function load() {
    const next = await api<ParentDashboardData>("/api/parent/dashboard");
    setData(next);
    if (!next.contentReview) {
      setTab((t) => (t === "review" ? "overview" : t));
      const search = leaveReviewQuery(window.location.search);
      if (search !== window.location.search.replace(/^\?/, "")) window.history.replaceState(null, "", window.location.pathname + (search ? "?" + search : "") + window.location.hash);
    }
  }

  async function loadCrew() {
    const result = await api<{ students: StudentSettings[] }>("/api/parent/students");
    setStudents(result.students);
  }

  // Teacher review is for reviewer families and the founding household only (the Worker decides). A
  // ?admin=review link opened by anyone else lands on the overview.
  const canReview = data?.contentReview === true;
  useEffect(() => { void load().catch(e=>setError(e instanceof Error?e.message:"Could not load your family dashboard")); }, []);
  useEffect(() => {
    if (tab === "crew" || tab === "library") void loadCrew();
  }, [tab]);

  if (!data) return <main className="loader-screen parent-club-loading"><div className="loader-orbit"><SummitMark size={38} /></div><span>{error||"Opening your family dashboard…"}</span>{error&&<><button className="button primary" onClick={()=>void load().catch(e=>setError(e.message))}>Try again</button><button className="button ghost" onClick={()=>void onLogout()}>Back to sign-in</button></>}</main>;

  async function verify(entry: VerifyEntry, amount: number) {
    try {
      await api(`/api/parent/entries/${entry.id}/verify`, { method: "POST", body: JSON.stringify({ amount }) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not verify entry"); }
  }

  async function verifyAll(entries: VerifyEntry[]) {
    try {
      for (const entry of entries) {
        await api(`/api/parent/entries/${entry.id}/verify`, { method: "POST", body: JSON.stringify({ amount: entry.amount }) });
      }
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not verify entries"); }
  }

  async function resolveRedemption(id: string, decision: "approved" | "declined") {
    if (!window.confirm(`${decision === "approved" ? "Approve" : "Decline"} this request?`)) return;
    try {
      await api(`/api/parent/redemptions/${id}/resolve`, { method: "POST", body: JSON.stringify({ decision, note: null }) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not resolve request"); }
  }

  // A parent signed in as a child, played, and the runs landed on the child's
  // record. This hands them back, without touching any mountain they cleared.
  async function resetRuns(studentId: string, name: string) {
    if (!window.confirm(`Give ${name} back today's ${BRAND.gameName} turns? Completed levels stay complete.`)) return;
    try {
      const result = await api<{ removed: number; keptSummits: number }>(
        `/api/parent/students/${studentId}/game/reset-today`, { method: "POST" },
      );
      await load();
      setError(result.removed === 0 ? `Nothing to give back. ${name} has only completed levels logged today.` : "");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not reset the runs"); }
  }

  async function adjustment(studentId: string, name: string) {
    const rawDelta = window.prompt(`Ledger correction for ${name}. Use a positive or negative whole number. Corrections only — never punishment.`);
    if (!rawDelta) return;
    const delta = Number(rawDelta);
    const note = window.prompt("Required bookkeeping note:");
    if (!Number.isInteger(delta) || delta === 0 || !note) return;
    try {
      await api(`/api/parent/students/${studentId}/ledger-adjustment`, { method: "POST", body: JSON.stringify({ delta, note }) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not correct ledger"); }
  }

  const queueCount = data.verifyQueue.length;
  const attentionCount = data.redemptions.length + data.wishlistPending;

  if (kidPreview) {
    return (
      <StudentDashboard
        onLogout={onLogout}
        previewStudentId={kidPreview}
        onReviewMath={()=>{setMathLearnerId(kidPreview);setKidPreview(null);setTab('math');}}
        onExitPreview={() => { setKidPreview(null); void load(); }}
      />
    );
  }

  return (
    <main className="parent-shell parent-club">
      <aside className="parent-sidebar">
        <BrandLockup appName={data.settings.appName} width={202} markSize={38} />
        <p className="sidebar-label">Parent dashboard</p>
        <nav>
          <NavButton active={tab === "math"} onClick={() => changeTab("math")} icon={<Compass />} label="Learning" />
          <NavButton secondary active={tab === "curriculum"} onClick={() => changeTab("curriculum")} icon={<ClipboardText />} label="Course browser" />
          {canReview && <NavButton secondary active={tab === "review"} onClick={() => changeTab("review")} icon={<ClipboardText />} label="Teacher review" />}
          <NavButton active={tab === "overview"} onClick={() => changeTab("overview")} icon={<Gauge />} label="Overview" />
          <NavButton active={tab === "verify"} onClick={() => changeTab("verify")} icon={<ClipboardText />} label="Review activity" badge={queueCount} />
          <NavButton active={tab === "crew"} onClick={() => changeTab("crew")} icon={<UsersThree />} label="Students" />
          <NavButton secondary active={tab === "library"} onClick={() => changeTab("library")} icon={<Gift />} label="Prize library" badge={attentionCount} />
          <NavButton secondary active={tab === "guide"} onClick={() => changeTab("guide")} icon={<Compass />} label="Start here" />
          <NavButton secondary active={tab === "settings"} onClick={() => changeTab("settings")} icon={<Gear />} label="Settings" />
          <NavButton secondary active={tab === "notifications"} icon={<EnvelopeSimple />} label="Notifications" onClick={() => changeTab("notifications")} />
          {/* Phones have room for four tabs in the bottom bar; the rest, and Sign out, live behind More. */}
          <button ref={moreButtonRef} className={`nav-more ${moreOpen || SECONDARY_TABS.includes(tab) ? "active" : ""}`} aria-label="More" aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)}><DotsThreeOutline /><span>More</span>{Boolean(attentionCount) && <b>{attentionCount}</b>}</button>
        </nav>
        {moreOpen && <div className="nav-more-sheet" role="menu" aria-label="More">
          <div className="nav-more-brand" role="presentation"><BrandLockup appName={data.settings.appName} width={260} markSize={38} /></div>
          <button role="menuitem" onClick={() => { changeTab("curriculum"); setMoreOpen(false); }}><ClipboardText /> Course browser</button>
          {canReview && <button role="menuitem" onClick={() => { changeTab("review"); setMoreOpen(false); }}><ClipboardText /> Teacher review</button>}
          <button role="menuitem" onClick={() => { changeTab("library"); setMoreOpen(false); }}><Gift /> Prize library{Boolean(attentionCount) && <b>{attentionCount}</b>}</button>
          <button role="menuitem" onClick={() => { changeTab("guide"); setMoreOpen(false); }}><Compass /> Start here</button>
          <button role="menuitem" onClick={() => { changeTab("settings"); setMoreOpen(false); }}><Gear /> Settings</button>
          <button role="menuitem" onClick={() => { changeTab("notifications"); setMoreOpen(false); }}><EnvelopeSimple /> Notifications</button>
          <button role="menuitem" onClick={() => void onLogout()}><SignOut /> Sign out</button>
          <p className="nav-more-legal"><a href="/privacy" target="_blank" rel="noopener">Privacy</a> · <a href="/terms" target="_blank" rel="noopener">Terms</a></p>
        </div>}
        <div className="sidebar-rule" />
        <button className="sidebar-logout" onClick={() => void onLogout()}><SignOut /> Sign out</button>
        <p className="sidebar-motto">Math confidence.<br /><strong>One day at a time.</strong></p>
        <p className="sidebar-legal"><button className="text-link" onClick={() => changeTab("guide")}>Help</button> · <a href="/privacy" target="_blank" rel="noopener">Privacy</a> · <a href="/terms" target="_blank" rel="noopener">Terms</a></p>
      </aside>

      <section className="parent-main">
        <div className="parent-mobile-brand"><BrandLockup appName={data.settings.appName} width={260} markSize={38} /></div>
        <header className="parent-topbar">
          <div><span className="eyebrow">{formatDate(data.today)}</span><h1>{tabTitle(tab)}</h1></div>
          <div className="topbar-actions"><span className="system-status"><i /> {queueCount ? `${queueCount} to review` : "All caught up"}</span><button className="icon-button" aria-label="Refresh" title="Refresh" onClick={() => void load()}><ArrowClockwise /></button></div>
        </header>
        {error && <div className="error-banner"><span>{error}</span><button aria-label="Dismiss message" onClick={() => setError("")}><X /></button></div>}
      {masteryFor && <MasteryBonusForm key={masteryFor.id} student={masteryFor} send={masteryFor.send} currency={BRAND.currency} onClose={() => setMasteryFor(null)} onSaved={load} />}

        {tab === "overview" && <MembershipCard startOnly />}
        {tab === "overview" && data.owedPrizes.length > 0 && (
          <OwedPanel owed={data.owedPrizes} onFulfil={fulfil} />
        )}
        {tab === "overview" && data.students.length === 0 && <FamilySetup onDone={load} />}
        {tab === "overview" && <GettingStartedCard students={data.students} />}
        {tab === "overview" && data.students.length > 0 && (
          <button className="try-run" onClick={() => setTryRun(true)}>
            <span aria-hidden="true"><Sparkle weight="fill" /></span>
            <span>
              <strong>Try {BRAND.gameName} yourself</strong>
              <small>Try every level without a daily limit or changing your child’s record.</small>
            </span>
            <span className="try-run-go">Play</span>
          </button>
        )}
        {tab === "overview" && data.students.length > 0 && (
          <Overview data={data} onGradesConfirmed={load} onVerify={() => setTab("verify")} onCrew={() => setTab("crew")} onResolve={resolveRedemption} onKidView={setKidPreview} onToggleGame={toggleGame} onOpenCirculation={() => setCirculationOpen(true)} onResetRuns={resetRuns} onTestPrep={(id) => { setMathLearnerId(id); setTab("math"); }} />
        )}
        {tab === "overview" && data.students.length > 0 && <Suspense fallback={<p>Opening weekly reports…</p>}><WeeklyMathReports students={data.students}/></Suspense>}
        {tab === "curriculum" && <Suspense fallback={<p>Opening courses and questions…</p>}><ContentBrowser/></Suspense>}
        {tab === "review" && canReview && <Suspense fallback={<p>Opening the teacher review…</p>}><ContentReview/></Suspense>}
        {tab === "math" && <Suspense fallback={<p>Opening saved learning evidence…</p>}><MathParent key={mathLearnerId??'all'} students={data.students} initialStudentId={mathLearnerId} currency={BRAND.currency} /></Suspense>}
        {tab === "guide" && <GuideTab currency={BRAND.currency} />}
        {tab === "verify" && <><ScanCard students={data.students} onApplied={load} onError={setError} /><VerifyQueue entries={data.verifyQueue} onVerify={verify} onVerifyAll={verifyAll} /></>}
        {tab === "crew" && (
          <section className="admin-section parent-club-content">
            <div className="section-heading"><div><span className="section-kicker"><UsersThree weight="fill" /> Student settings</span><h2>Your students</h2><p>Grades and daily goals are easiest to change at the start of a school year.</p></div><button className="button primary" onClick={() => setStudentEditor("new")}><Plus weight="bold" /> Add student</button></div>
            <div className="crew-admin-grid">
              {students.map((student) => (
                <article className="admin-card" key={student.id}>
                  <div className="avatar">{initials(student.name)}</div><div className="admin-card-main"><h3>{student.name}</h3><p>{student.grade != null ? gradeLabel(student.grade) : BAND_DEFAULTS[student.band as Band]?.label} · {student.daily_target} {unitLabel(student.unit_type, student.daily_target)} daily</p><div className="chip-row"><span>{JSON.parse(student.off_weekdays).length || 0} weekly off-days</span><span>{student.offDays.length} dated off-days</span>{!student.active && <span className="inactive-chip">Inactive</span>}</div></div>
                  <div className="admin-card-actions"><button className="small-button" onClick={() => setStudentEditor(student)}><PencilSimple /> Edit</button><button className="small-button" onClick={() => { setMathLearnerId(student.id); setTab("math"); }}><Compass /> Learning evidence</button><button className="small-button" onClick={() => { setMathLearnerId(student.id); setTab("math"); }}>📝 Test prep</button>{parentBonusAvailable(data.settings) && <button className="small-button" disabled={masteryFor !== null} onClick={() => setMasteryFor({ ...student, send: masteryBonusSubmission(request => api<{ ok: boolean }>(`/api/parent/students/${student.id}/bonus`, { method: "POST", body: JSON.stringify(request) })) })}><Medal /> Add parent bonus</button>}<button className="small-button" onClick={()=>setCertificatesFor({id:student.id,name:student.name})}>Certificates</button><button className="small-button" onClick={() => setAuditFor({ id: student.id, name: student.name })}><SlidersHorizontal /> Ledger</button><a className="small-button" href={`/api/parent/students/${student.id}/export`} download>Export data</a><button className="small-button" onClick={() => void deleteChild(student.id, student.name, async () => { await loadCrew(); await load(); })}>Delete</button></div>
                </article>
              ))}
            </div>
          </section>
        )}
        {tab === "library" && (
          <LibraryTab
            students={students.filter((student) => student.active)}
            currency={BRAND.currency}
            redemptions={data.redemptions}
            onResolveRedemption={resolveRedemption}
            onAddCustom={() => setPrizeEditor("new")}
            onEdit={setPrizeEditor}
            onError={setError}
            onChanged={load}
          />
        )}
        {tab === "notifications" && <Suspense fallback={<p>Opening notifications…</p>}><ParentNotifications/></Suspense>}
        {tab === "settings" && <FamilySettings settings={data.settings} onSaved={load} />}
      </section>

      {studentEditor && <StudentEditor student={studentEditor} onClose={() => setStudentEditor(null)} onSaved={async () => { setStudentEditor(null); await Promise.all([loadCrew(), load()]); }} />}
      {certificatesFor&&<Modal title={`${certificatesFor.name} certificates`} onClose={()=>setCertificatesFor(null)}><CertificatesPanel studentId={certificatesFor.id}/></Modal>}
      {prizeEditor && <PrizeEditor prize={prizeEditor} students={data.students} currency={BRAND.currency} onClose={() => setPrizeEditor(null)} onSaved={async () => { setPrizeEditor(null); await load(); }} />}
      {tryRun && <SummitRun band="C" sandbox onClose={() => setTryRun(false)} />}
      {circulationOpen && (
        <Circulation
          students={data.students}
          currency={BRAND.currency}
          onClose={() => setCirculationOpen(false)}
          onOpenLedger={(id, name) => { setCirculationOpen(false); setAuditFor({ id, name }); }}
        />
      )}
      {auditFor && (
        <LedgerAudit
          studentId={auditFor.id}
          name={auditFor.name}
          currency={BRAND.currency}
          onClose={() => setAuditFor(null)}
          onCorrect={() => void adjustment(auditFor.id, auditFor.name)}
        />
      )}
    </main>
  );
}

const SECONDARY_TABS: string[] = ["curriculum", "review", "library", "guide", "settings", "notifications"];
function NavButton({ active, onClick, icon, label, badge, secondary = false }: { active: boolean; onClick: () => void; icon: ReactNode; label: string; badge?: number; secondary?: boolean }) {
  return <button aria-label={label} className={[active ? "active" : "", secondary ? "nav-secondary" : ""].filter(Boolean).join(" ")} onClick={onClick}>{icon}<span>{label}</span>{Boolean(badge) && <b>{badge}</b>}</button>;
}

// Children added before grades were recorded have a guessed grade; ask once so the next course
// choices (and new children's starting courses) use the real one.
export function ConfirmGrades({ students, onSaved }: { students: ParentDashboardData["students"]; onSaved: () => Promise<void> }) {
  const [choices, setChoices] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(""), [error, setError] = useState("");
  if (students.length === 0) return null;
  async function confirm(id: string, grade: number) {
    setBusy(id); setError("");
    try { await api(`/api/parent/students/${id}/grade`, { method: "PUT", body: JSON.stringify({ grade }) }); await onSaved(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save the grade"); }
    finally { setBusy(""); }
  }
  return <article className="confirm-grades"><h3>Confirm each child’s grade</h3><p>{BRAND.shortName} uses grade to choose the right course. We guessed from the age range you picked earlier.</p>
    {students.map((student) => { const grade = choices[student.id] ?? student.grade ?? GRADE_FROM_BAND[student.band as keyof typeof GRADE_FROM_BAND] ?? 0; return <div className="confirm-grade-row" key={student.id}><strong>{student.name}</strong>
      <select aria-label={`${student.name}’s grade`} value={grade} onChange={(event) => setChoices({ ...choices, [student.id]: Number(event.target.value) })}>{GRADES.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</select>
      <button className="small-button" disabled={busy !== ""} onClick={() => void confirm(student.id, grade)}>{busy === student.id ? "Saving…" : `Confirm ${gradeLabel(grade)}`}</button></div>; })}
    {error && <p className="form-error" role="alert">{error}</p>}</article>;
}

function Overview({ data, onVerify, onCrew, onResolve, onKidView, onToggleGame, onOpenCirculation, onResetRuns, onGradesConfirmed, onTestPrep }: { data: ParentDashboardData; onTestPrep: (id: string) => void; onGradesConfirmed: () => Promise<void>; onVerify: () => void; onCrew: () => void; onResolve: (id: string, decision: "approved" | "declined") => Promise<void>; onKidView: (id: string) => void; onToggleGame: (id: string, enabled: boolean) => Promise<void>; onOpenCirculation: () => void; onResetRuns: (id: string, name: string) => Promise<void> }) {
  const summited = data.students.filter((student) => student.liftedOff).length;
  return (
    <section className="overview-stack parent-club-content">
      <div className="overview-metrics">
        <article><span className="metric-icon"><CheckCircle weight="fill" /></span><div><small>Daily goal reached</small><strong>{summited}<span>/ {data.students.length}</span></strong></div></article>
        <article><span className="metric-icon blue"><ClipboardText weight="fill" /></span><div><small>Awaiting verification</small><strong>{data.verifyQueue.length}</strong></div></article>
        <article className="metric-clickable" role="button" tabIndex={0}
          onClick={() => onOpenCirculation()}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpenCirculation(); } }}
        ><span className="metric-icon gold"><Coins weight="fill" /></span><div><small>Total {BRAND.currency} in circulation</small><strong>{data.students.reduce((sum, student) => sum + student.balances.balance, 0).toLocaleString()}</strong></div><span className="metric-more">Open the books →</span></article>
      </div>
      <ConfirmGrades students={data.students.filter((student) => student.active && !student.grade_confirmed)} onSaved={onGradesConfirmed} />
      <div className="section-heading"><div><span className="section-kicker"><UsersThree weight="fill" /> Today at a glance</span><h2>Your students</h2></div><button className="text-link" onClick={onCrew}>Manage students <PencilSimple /></button></div>
      <div className="student-glance-grid">
        {data.students.map((student) => {
          const progress = Math.min(100, Math.round((student.todayAmount / student.daily_target) * 100));
          return <article className={`student-glance ${student.liftedOff ? "airborne" : ""}`} key={student.id}><div className="glance-top"><div className="avatar">{initials(student.name)}</div><div><h3>{student.name}</h3><span>{student.grade != null ? gradeLabel(student.grade) : BAND_DEFAULTS[student.band as Band]?.grades}</span></div><span className="flight-chip">{student.liftedOff ? <><CheckCircle weight="fill" /> Goal reached</> : "In progress"}</span></div><div className="glance-progress"><div><span>Today</span><strong>{student.todayAmount} / {student.daily_target} {unitLabel(student.unit_type, student.daily_target)}</strong></div><div className="progress-track"><i style={{ width: `${progress}%` }} /></div></div><div className="glance-stats"><span><strong>{student.mathXp?.today??0}</strong> Math XP today · {student.mathXp?.total??0} total</span><span><CheckCircle weight="fill" /> <strong>{student.streak.current}</strong> day streak · 🧊 {student.streak.freezes??1} {(student.streak.freezes??1)===1?"freeze":"freezes"}</span><span><Coins /> <strong>{student.balances.balance}</strong> {BRAND.currency}</span>{student.savingsGoal&&<span><Gift/> Saving for {student.savingsGoal.name} · {student.savingsGoal.progress}%</span>}<span><ClipboardText /> <strong>{student.unverifiedCount}</strong> to verify</span></div><div className="glance-actions"><button className="small-button" onClick={() => onKidView(student.id)}>👀 See {student.name}'s view</button><button className="small-button" onClick={() => onTestPrep(student.id)}>📝 {student.testPrep && student.testPrep.daysLeft >= 0 ? `${countdownText(student.testPrep.daysLeft)}${student.testPrep.setBy === "child" ? ` · set by ${student.name}` : ""}` : "Practice for a test"}</button><button className="small-button" onClick={() => void onToggleGame(student.id, !Number(student.game_enabled))}>{Number(student.game_enabled) ? `${BRAND.gameName} on` : `${BRAND.gameName} off`}</button>{Number(student.game_enabled) === 1 && <span className="glance-runs">{Number(student.gameRunsToday ?? 0)} of {Number(student.gameRunsPerDay ?? 3)} runs played today</span>}{Number(student.game_enabled) === 1 && Number(student.gameRunsToday ?? 0) > 0 && <button className="small-button" onClick={() => void onResetRuns(student.id, student.name)}>↺ Give the runs back</button>}</div></article>;
        })}
        {data.students.length === 0 && <EmptyState icon={<UsersThree />} title="No students yet" body="Add your first child to set up their learning and daily goals." action="Add student" onAction={onCrew} />}
      </div>
      <div className="overview-lower">
        <section className="queue-preview"><div className="section-heading"><div><span className="section-kicker"><ClipboardText weight="fill" /> Activity review</span><h2>Review activity</h2></div>{data.verifyQueue.length > 0 && <button className="text-link" onClick={onVerify}>Review all</button>}</div>{data.verifyQueue.slice(0, 4).map((entry) => <div className="queue-row" key={entry.id}><div className="avatar small">{initials(entry.studentName)}</div><div><strong>{entry.studentName}</strong><span>{formatShortDate(entry.date)}</span></div><b>{entry.amount} {unitLabel(entry.unitType, entry.amount)}</b><span className="status-dot">Unverified</span></div>)}{data.verifyQueue.length === 0 && <p className="empty-line"><CheckCircle weight="fill" /> All activity has been reviewed.</p>}</section>
        <section className="queue-preview"><div className="section-heading"><div><span className="section-kicker"><Gift weight="fill" /> Requests</span><h2>Prize approvals</h2></div></div><RedemptionRows rows={data.redemptions.slice(0, 3)} onResolve={onResolve} compact /></section>
      </div>
    </section>
  );
}

function VerifyQueue({ entries, onVerify, onVerifyAll }: { entries: VerifyEntry[]; onVerify: (entry: VerifyEntry, amount: number) => Promise<void>; onVerifyAll: (entries: VerifyEntry[]) => Promise<void> }) {
  const [busyAll, setBusyAll] = useState(false);
  return <section className="admin-section verify-section parent-club-content"><div className="section-heading"><div><span className="section-kicker"><ClipboardText weight="fill" /> Weekly check</span><h2>{entries.length ? `${entries.length} entries ready` : "Queue clear"}</h2><p>Adjust the number if needed, then verify. The Sunday Math Academy email auto-verifies most weeks.</p></div>{entries.length > 1 && <button className="button primary" disabled={busyAll} onClick={async () => { setBusyAll(true); await onVerifyAll(entries); setBusyAll(false); }}><Check weight="bold" /> {busyAll ? "Verifying…" : "Verify all as logged"}</button>}</div>{entries.length === 0 ? <EmptyState icon={<CheckCircle />} title="Everything is verified" body="There is no activity waiting for review." /> : <div className="verify-table"><div className="verify-head"><span>Student</span><span>Date</span><span>Target</span><span>Logged</span><span /></div>{entries.map((entry) => <VerifyRow key={entry.id} entry={entry} onVerify={onVerify} />)}</div>}</section>;
}

function VerifyRow({ entry, onVerify }: { entry: VerifyEntry; onVerify: (entry: VerifyEntry, amount: number) => Promise<void> }) {
  const [amount, setAmount] = useState(entry.amount);
  const [busy, setBusy] = useState(false);
  return <div className="verify-row"><span className="verify-student"><span className="avatar small">{initials(entry.studentName)}</span><strong>{entry.studentName}</strong></span><span>{formatShortDate(entry.date)}</span><span>{entry.target} {unitLabel(entry.unitType, entry.target)}</span><label><input type="number" min={0} value={amount} onChange={(event) => setAmount(event.currentTarget.valueAsNumber)} /><small>{unitLabel(entry.unitType, amount)}</small></label><button className="button success" disabled={busy || !Number.isInteger(amount)} onClick={async () => { setBusy(true); await onVerify(entry, amount); setBusy(false); }}><Check weight="bold" /> {busy ? "Saving" : "Verify"}</button></div>;
}

// ---------------------------------------------------------------------------
// The three-layer prize model, parent side: full library with search/filter,
// per-kid storefront activation, and the wishlist review queue.
// ---------------------------------------------------------------------------
function LibraryTab({ students, currency, redemptions, onResolveRedemption, onAddCustom, onEdit, onError, onChanged }: {
  students: StudentSettings[];
  currency: string;
  redemptions: Array<Record<string, string | number>>;
  onResolveRedemption: (id: string, decision: "approved" | "declined") => Promise<void>;
  onAddCustom: () => void;
  onEdit: (prize: Prize) => void;
  onError: (message: string) => void;
  onChanged: () => Promise<void>;
}) {
  const [library, setLibrary] = useState<LibraryData | null>(null);
  const [wishes, setWishes] = useState<ParentWishRow[]>([]);
  const [search, setSearch] = useState("");
  const [difficulties, setDifficulties] = useState<Record<string, Difficulty>>({});
  const [suggesting, setSuggesting] = useState("");
  const [suggested, setSuggested] = useState<{ studentId: string; count: number; note?: string } | null>(null);
  const [tier, setTier] = useState("");
  const [band, setBand] = useState("");
  const [category, setCategory] = useState("");
  const [maxDrive, setMaxDrive] = useState("");
  const [costBand, setCostBand] = useState("");
  const [importing, setImporting] = useState(false);

  async function loadLibrary() {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (tier) params.set("tier", tier);
    if (band) params.set("band", band);
    if (category) params.set("category", category);
    if (maxDrive) params.set("maxDrive", maxDrive);
    if (costBand) params.set("costBand", costBand);
    const [lib, wish] = await Promise.all([
      api<LibraryData>(`/api/parent/library?${params}`),
      api<{ wishlist: ParentWishRow[] }>("/api/parent/wishlist"),
    ]);
    setLibrary(lib);
    setWishes(wish.wishlist);
  }

  useEffect(() => { void loadLibrary();   }, [search, tier, band, category, maxDrive, costBand]);

  async function toggle(studentId: string, prizeId: string, active: boolean) {
    try {
      await api("/api/parent/storefront", { method: "POST", body: JSON.stringify({ studentId, prizeId, active }) });
      await loadLibrary();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Could not update the storefront"); }
  }

  async function runImport() {
    setImporting(true);
    try {
      const result = await api<{ imported: number; skipped: number }>("/api/parent/library/import", { method: "POST", body: "{}" });
      onError("");
      window.alert(`Catalog import: ${result.imported} added, ${result.skipped} already present.`);
      await loadLibrary();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Import failed"); }
    setImporting(false);
  }

  async function resolveWish(wish: ParentWishRow, decision: "approved" | "declined") {
    try {
      await api(`/api/parent/wishlist/${wish.id}/resolve`, { method: "POST", body: JSON.stringify({ decision }) });
      await Promise.all([loadLibrary(), onChanged()]);
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Could not resolve wish"); }
  }

  const activationSet = new Set((library?.activations ?? []).map((row) => `${row.student_id}:${row.prize_id}`));
  const pendingWishes = wishes.filter((wish) => wish.status === "pending");

  async function suggestFor(studentId: string, difficulty: Difficulty) {
    setSuggesting(studentId);
    try {
      const result = await api<{ added: Array<{ name: string }>; note?: string }>("/api/parent/storefront/suggest", {
        method: "POST", body: JSON.stringify({ studentId, difficulty }),
      });
      setSuggested({ studentId, count: result.added.length, note: result.note });
      await Promise.all([loadLibrary(), onChanged()]);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not build a starter set");
    } finally { setSuggesting(""); }
  }

  return (
    <section className="admin-section parent-club-content">
      <div className="starter-panel">
        <div className="section-heading"><div>
          <span className="section-kicker"><Sparkle weight="fill" /> Quick start</span>
          <h2>Fill a store in one click</h2>
          <p>Proposes a ladder of band-appropriate prizes across categories, then you edit it down below. Running it again tops up rather than duplicating, and it never re-adds something already in their store.</p>
        </div></div>
        <ul className="starter-list">
          {students.map((student) => {
            const owned = (library?.activations ?? []).filter((row) => row.student_id === student.id).length;
            return (
              <li key={student.id}>
                <div>
                  <strong>{student.name}</strong>
                  <small>{BAND_DEFAULTS[student.band as Band]?.grades} · {owned} {owned === 1 ? "prize" : "prizes"} in store</small>
                </div>
                <div className="starter-actions">
                  <select
                    value={difficulties[student.id] ?? "balanced"}
                    onChange={(event) => setDifficulties({ ...difficulties, [student.id]: event.target.value as Difficulty })}
                  >
                    {(Object.keys(DIFFICULTY_LABELS) as Difficulty[]).map((key) => (
                      <option key={key} value={key}>{DIFFICULTY_LABELS[key]}</option>
                    ))}
                  </select>
                  <button
                    className="button primary"
                    disabled={suggesting === student.id}
                    onClick={() => void suggestFor(student.id, difficulties[student.id] ?? "balanced")}
                  >
                    {suggesting === student.id ? "Building…" : "Suggest a starter set"}
                  </button>
                </div>
                <small className="starter-shape">{starterSetShape(difficulties[student.id] ?? "balanced")}</small>
                {suggested?.studentId === student.id && (
                  <span className="saved-note"><CheckCircle weight="fill" /> {suggested.note ?? `Added ${suggested.count}`}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {redemptions.length > 0 && (
        <section className="redemption-section"><div className="section-heading"><div><span className="section-kicker"><Sparkle weight="fill" /> Pending approvals</span><h2>Ready to redeem</h2></div></div><RedemptionRows rows={redemptions} onResolve={onResolveRedemption} /></section>
      )}

      {pendingWishes.length > 0 && (
        <section className="redemption-section wish-queue">
          <div className="section-heading"><div><span className="section-kicker"><Star weight="fill" /> Wishlist queue</span><h2>{pendingWishes.length} wish{pendingWishes.length === 1 ? "" : "es"} to review</h2><p>AI triage suggests a tier and flags concerns; you decide. Approving adds it to the library and that kid's store.</p></div></div>
          {pendingWishes.map((wish) => {
            const triage = wish.ai_triage ? JSON.parse(wish.ai_triage) as { estimated_cost_band?: string; suggested_tier?: number; category?: string; concerns?: string[]; one_line_summary?: string } : null;
            return (
              <div className="wish-row" key={wish.id}>
                <div className="wish-main">
                  <strong>{wish.student_name} wishes for: {wish.raw_name}</strong>
                  {wish.note && <span className="muted">"{wish.note}"</span>}
                  {wish.url && <a className="text-link" href={wish.url} target="_blank" rel="noreferrer">link ↗</a>}
                  {triage ? (
                    <span className="wish-triage">
                      AI: {triage.estimated_cost_band ?? "?"} · Tier {triage.suggested_tier ?? "?"} · {triage.category ?? "?"}
                      {triage.one_line_summary ? ` — ${triage.one_line_summary}` : ""}
                      {(triage.concerns?.length ?? 0) > 0 && <em className="wish-concerns"> ⚠ {triage.concerns!.join("; ")}</em>}
                    </span>
                  ) : <span className="wish-triage muted">No AI triage (still running, or key not set) — review manually.</span>}
                </div>
                <div className="redemption-actions">
                  <button className="small-button approve" onClick={() => void resolveWish(wish, "approved")}><Check /> Approve to store</button>
                  <button className="small-button" onClick={() => void resolveWish(wish, "declined")}>Decline</button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      <div className="section-heading">
        <div><span className="section-kicker"><Gift weight="fill" /> Library</span><h2>Prize library</h2><p>Activate items into each kid's store — aim for 4 to 6 per tier. Kids never see this view.</p></div>
        <div className="row-buttons">
          <button className="small-button" disabled={importing} onClick={() => void runImport()}>{importing ? "Importing…" : "Import seed catalog"}</button>
          <button className="button primary" onClick={onAddCustom}><Plus weight="bold" /> Custom prize</button>
        </div>
      </div>

      <div className="library-filters">
        <input placeholder="Search prizes…" value={search} onChange={(event) => setSearch(event.target.value)} />
        <select value={tier} onChange={(event) => setTier(event.target.value)}><option value="">Any tier</option>{[1, 2, 3, 4].map((t) => <option key={t} value={t}>Tier {t}</option>)}</select>
        <select value={band} onChange={(event) => setBand(event.target.value)}><option value="">Any grade</option>{(Object.keys(BAND_DEFAULTS) as Band[]).map((b) => <option key={b} value={b}>{BAND_DEFAULTS[b].label}</option>)}</select>
        <select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">Any category</option>{Object.entries(CATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
        <select value={maxDrive} onChange={(event) => setMaxDrive(event.target.value)}><option value="">Any drive</option><option value="30">≤ 30 min</option><option value="60">≤ 1 hour</option><option value="120">≤ 2 hours</option></select>
        <select value={costBand} onChange={(event) => setCostBand(event.target.value)}><option value="">Any cost</option><option value="$">$</option><option value="$$">$$</option><option value="$$$">$$$</option></select>
      </div>

      <div className="library-list">
        {(library?.prizes ?? []).map((prize) => {
          const eligible = prize.eligible_bands ? JSON.parse(prize.eligible_bands) as Band[] : (["A", "B", "C", "D", "E"] as Band[]);
          return (
            <article className="library-row" key={prize.id}>
              <div className={`prize-admin-icon tier-tint-${prize.tier}`}>{prize.image_url ? <img src={prize.image_url} alt="" /> : <PrizeArt name={prize.name} />}</div>
              <div className="library-main">
                <div className="library-title"><h3>{prize.name}</h3><span className={`tier-badge tier-${prize.tier}`}>T{prize.tier}</span>{prize.category && <span className="cat-chip">{CATEGORY_LABELS[prize.category as PrizeCategory] ?? prize.category}</span>}{prize.cost_band && <span className="cat-chip">{prize.cost_band}</span>}{prize.drive_minutes != null && <span className="cat-chip">{prize.drive_minutes} min drive</span>}{prize.source === "wishlist" && <span className="cat-chip wish-chip-tag">wish</span>}</div>
                {prize.description && <p>{prize.description}</p>}
                <div className="library-toggles">
                  {students.map((student) => {
                    const on = activationSet.has(`${student.id}:${prize.id}`);
                    const bandOk = eligible.includes(student.band as Band);
                    return (
                      <button
                        key={student.id}
                        className={`kid-toggle ${on ? "on" : ""} ${bandOk ? "" : "off-band"}`}
                        title={bandOk ? `${on ? "Remove from" : "Add to"} ${student.name}'s store (${effectiveCost(prize, student.band as Band)} ${currency})` : `Outside the suggested bands, still allowed`}
                        onClick={() => void toggle(student.id, prize.id, !on)}
                      >
                        {on ? <Check weight="bold" /> : <Plus weight="bold" />} {student.name}
                        <small>{effectiveCost(prize, student.band as Band)}</small>
                      </button>
                    );
                  })}
                  <button className="icon-button" onClick={() => onEdit(prize)} title="Edit"><PencilSimple /></button>
                </div>
              </div>
            </article>
          );
        })}
        {library && library.prizes.length === 0 && <EmptyState icon={<Gift />} title="No matches" body="Loosen the filters, or import the seed catalog to fill the library." />}
      </div>
    </section>
  );
}

export function RedemptionRows({ rows, onResolve, compact = false }: { rows: Array<Record<string, string | number>>; onResolve: (id: string, decision: "approved" | "declined") => Promise<void>; compact?: boolean }) {
  if (rows.length === 0) return <p className="empty-line"><CheckCircle weight="fill" /> No prize requests waiting.</p>;
  return <div className="redemption-rows">{rows.map((row) => <div className="redemption-row" key={String(row.id)}><span className="prize-request-icon"><Gift weight="duotone" /></span><div><strong>{String(row.student_name)}</strong><span>{String(row.prize_name_snapshot)} · {Number(row.cost_snapshot).toLocaleString()}</span></div><div className="redemption-actions"><button className="small-button approve" onClick={() => void onResolve(String(row.id), "approved")}><Check /> Approve</button><button className="small-button" aria-label={`Decline ${String(row.student_name)}’s request for ${String(row.prize_name_snapshot)}`} onClick={() => void onResolve(String(row.id), "declined")}>{compact ? <X /> : "Decline"}</button></div></div>)}</div>;
}

export function StudentEditor({ student, onClose, onSaved }: { student: StudentSettings | "new"; onClose: () => void; onSaved: () => Promise<void> }) {
  const isNew = student === "new";
  const existing = isNew ? null : student;
  const formId = useId();
  const [grade, setGrade] = useState<number | "">(existing ? existing.grade ?? GRADE_FROM_BAND[existing.band] : "");
  const band: Band | "" = grade === "" ? "" : bandForGrade(grade);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const defaults = band ? BAND_DEFAULTS[band] : null;
  const useExistingValues = existing?.band === band;
  const selectedUnit = useExistingValues ? existing.unit_type : defaults?.unitType ?? "xp";
  const suggestedGoal = defaults?.unitType === "xp" ? defaultDailyXpGoalFor(grade === "" ? null : grade) : defaults?.dailyTarget;
  const selectedTarget = useExistingValues ? existing.daily_target : suggestedGoal;
  let offWeekdays: number[] = [];
  // New children have no days off by default (docs/plans/ONBOARDING.md decisions); parents add them.
  try { offWeekdays = existing ? JSON.parse(existing.off_weekdays) as number[] : []; } catch { offWeekdays = []; }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); if (grade === "") { setError("Choose your child’s grade before saving."); return; } setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    const dates = String(form.get("offDays") ?? "").split(/[\n,]/u).map((date) => date.trim()).filter(Boolean);
    const body = {
      name: form.get("name"), pin: form.get("pin") || undefined, grade,
      // lessonRate is legacy: a day at target is worth the same to every kid now,
      // so the server keeps the column but nothing reads it.
      unitType: form.get("unitType"), dailyTarget: Number(form.get("dailyTarget")), lessonRate: 0,
      dashboardLook: form.get("dashboardLook") || null,
      offWeekdays: form.getAll("offWeekdays").map(Number), offDays: dates.map((date) => ({ date })), active: form.get("active") === "on",
    };
    try {
      await api(isNew ? "/api/parent/students" : `/api/parent/students/${existing?.id ?? ""}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(body) });
      await onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save student"); setBusy(false); }
  }

  return <Modal title={isNew ? "Add a student" : `Edit ${existing?.name ?? "student"}`} onClose={onClose}><form className="modal-form" onSubmit={submit}><div className="form-grid two"><label htmlFor={`${formId}-name`}>Name<input id={`${formId}-name`} name="name" defaultValue={existing?.name} required /></label><label htmlFor={`${formId}-pin`}>{isNew ? "4-digit PIN" : "New PIN (optional)"}<input id={`${formId}-pin`} name="pin" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} required={isNew} /></label></div><label htmlFor={`${formId}-grade`}>Grade<select aria-label="Grade" id={`${formId}-grade`} required name="grade" value={grade} onChange={(event) => setGrade(event.target.value === "" ? "" : Number(event.target.value))}><option value="" disabled>Choose your child’s grade</option>{GRADES.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label>{defaults&&suggestedGoal!==undefined&&<div className="band-default-note"><CheckCircle weight="fill" /><span>Suggested goal: {suggestedGoal} {unitLabel(defaults.unitType, suggestedGoal)} a day. Choose what fits your child. Days off protect their streak.</span></div>}<div className="form-grid two" key={band}><label>Unit<select aria-label="Unit" disabled={!band} name="unitType" defaultValue={selectedUnit}><option value="xp">XP</option><option value="lessons">Lessons</option><option value="reading">Reading nights</option></select></label><label>Daily target<input aria-label="Daily target" name="dailyTarget" disabled={!band} type="number" min={1} defaultValue={selectedTarget} required /></label></div><label>Dashboard style<select name="dashboardLook" defaultValue={existing?.dashboard_look??""}><option value="">By grade (Adventure during preview)</option><option value="adventure">Adventure</option><option value="studio">Studio</option></select><small>Studio offers a quiet layout for older learners. The automatic Grade 4 default is off during review.</small></label><fieldset><legend>Scheduled weekly off-days</legend><div className="weekday-picker">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => <label key={day}><input type="checkbox" name="offWeekdays" value={index} defaultChecked={offWeekdays.includes(index)} /><span>{day}</span></label>)}</div></fieldset><label>Vacation / off dates <small>One YYYY-MM-DD date per line</small><textarea name="offDays" rows={4} defaultValue={existing?.offDays.map((day) => day.date).join("\n")} placeholder="2026-12-24&#10;2026-12-25" /></label><label className="toggle-line"><input type="checkbox" name="active" defaultChecked={existing ? Boolean(existing.active) : true} /><span>Student is active</span></label>{error && <p className="form-error" role="alert">{error}</p>}<div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy||!band}>{busy ? "Saving…" : "Save"}</button></div></form></Modal>;
}

function PrizeEditor({prize,students,currency,onClose,onSaved}:{prize:Prize|'new';students:Pick<StudentSettings,"unit_type"|"daily_target">[];currency:string;onClose:()=>void;onSaved:()=>Promise<void>}) {
 const isNew=prize==='new',existing=isNew?null:prize;
 const [busy,setBusy]=useState(false),[error,setError]=useState(''),[name,setName]=useState(existing?.name??'');
 const [target,setTarget]=useState(students.find(student=>student.unit_type==='xp')?.daily_target??ENGAGEMENT_CONFIG.defaultDailyXpGoal);
 const [tier,setTier]=useState(existing?.tier??1),[price,setPrice]=useState(existing?effectiveCost(existing,'C'):suggestedTierPrice(target,1));
 type PrizeBody={name:string;description:FormDataEntryValue|null;cost:number;tier:number;band:null;imageUrl:FormDataEntryValue|null;active:boolean;weeklyLimit:number|null;priceConfirmation?:number};
 const [pending,setPending]=useState<PrizeBody|null>(null);
 const previous=existing?effectiveCost(existing,'C'):0;
 async function save(body:PrizeBody){setBusy(true);setError('');try{await api(isNew?'/api/parent/prizes':`/api/parent/prizes/${existing!.id}`,{method:isNew?'POST':'PUT',body:JSON.stringify(body)});await onSaved();}catch(err){setError(err instanceof Error?err.message:'Could not save prize');setBusy(false);}}
 function submit(e:FormEvent<HTMLFormElement>){e.preventDefault();const f=new FormData(e.currentTarget),limit=String(f.get('weeklyLimit')??'');const body:PrizeBody={name,description:f.get('description')||null,cost:price,tier,band:null,imageUrl:f.get('imageUrl')||null,active:f.get('active')==='on',weeklyLimit:limit?Number(limit):null};if(existing&&price!==previous)setPending(body);else void save(body);}
 return <Modal title={isNew?'Add a parent prize':`Edit ${existing!.name}`} onClose={onClose}>
 <form className="modal-form" onSubmit={submit}><fieldset disabled={busy||pending!==null} hidden={pending!==null}>
  {isNew&&<fieldset><legend>Time together and choices make great prizes</legend>{EXPERIENCE_PRIZES.map(suggestion=><button className="small-button" type="button" key={suggestion} onClick={()=>setName(suggestion)}>{suggestion}</button>)}</fieldset>}
  <label>Prize name<input name="name" value={name} onChange={e=>setName(e.target.value)} maxLength={80} required/></label><label>Description<textarea name="description" maxLength={240} defaultValue={existing?.description??''}/></label>
  <div className="form-grid two"><label>Price in {currency}<input name="cost" type="number" min={1} max={1000000} value={price} onChange={e=>setPrice(Number(e.target.value))} required/></label><label>Tier<select name="tier" value={tier} onChange={e=>setTier(Number(e.target.value))}>{[1,2,3,4].map(t=><option key={t} value={t}>Tier {t} · about {ENGAGEMENT_CONFIG.stamps.tierGoalDays[t-1]} goal days</option>)}</select></label></div>
  <label>Daily XP target for a price suggestion<select value={target} onChange={e=>setTarget(Number(e.target.value))}>{[...new Set([target,ENGAGEMENT_CONFIG.defaultDailyXpGoal,...students.filter(student=>student.unit_type==='xp').map(student=>student.daily_target)])].map(value=><option key={value} value={value}>{value} XP per day</option>)}</select></label>
  <p>Suggested price: {suggestedTierPrice(target,tier)} {currency}. Changing a goal does not change any existing price.</p><button type="button" className="small-button" onClick={()=>setPrice(suggestedTierPrice(target,tier))}>Use suggested price</button>
  <label>Weekly limit per child (optional)<input name="weeklyLimit" type="number" min={1} max={100} defaultValue={existing?.weekly_limit??''}/></label><label>Image URL (optional)<input name="imageUrl" type="url" maxLength={500} defaultValue={existing?.image_url??''}/></label>
  <label className="toggle-line"><input name="active" type="checkbox" defaultChecked={existing?!!existing.active:true}/><span>Available in the library</span></label><p>Activate this prize in each child's store. Approval creates a promise; mark it delivered to deduct {BRAND.currency}.</p><div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy?'Saving…':'Save prize'}</button></div>
 </fieldset></form>
 {pending&&<PrizePriceConfirmation name={pending.name} previous={previous} next={pending.cost} currency={currency} busy={busy} onCancel={()=>setPending(null)} onConfirm={()=>void save({...pending,priceConfirmation:previous})}/>}
 {error&&<p className="form-error" role="alert">{error}</p>}
 </Modal>;
}

function FamilySettings({ settings, onSaved }: { settings: ParentDashboardData["settings"]; onSaved: () => Promise<void> }) {
  const [saved, setSaved] = useState(false); const [error, setError] = useState("");
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [syncLog, setSyncLog] = useState<Array<Record<string, string | null>>>([]);
  useEffect(() => { void api<{ log: Array<Record<string, string | null>> }>("/api/parent/synclog").then((r) => setSyncLog(r.log)).catch(() => {}); }, []);
  async function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); const form = new FormData(event.currentTarget); try { await api("/api/parent/settings", { method: "PUT", body: JSON.stringify({ timezone: form.get("timezone") }) }); setSaved(true); await onSaved(); } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save settings"); } }
  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setPasswordSaved(false);
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/parent/password", { method: "POST", body: JSON.stringify({ currentPassword: form.get("current"), newPassword: form.get("next") }) });
      setPasswordSaved(true);
      (event.target as HTMLFormElement).reset();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not change password"); }
  }
  return <section className="admin-section settings-section parent-club-content"><div className="section-heading"><div><span className="section-kicker"><Gear weight="fill" /> Your family</span><h2>Family settings</h2><p>Manage your timezone, security, membership, and family access.</p></div></div>
    <form className="settings-card parent-club-card" onSubmit={submit}><label>Family timezone<input name="timezone" defaultValue={settings.timezone} required /></label><div className="policy-note"><Coins weight="fill" /><div><strong>{BRAND.currency} belongs to the student.</strong><span>Negative ledger corrections exist only to repair bookkeeping. Never use them as punishment.</span></div></div>{error && <p className="form-error">{error}</p>}<button className="button primary">Save settings</button>{saved && <span className="saved-note"><CheckCircle weight="fill" /> Saved</span>}</form>
    <MembershipCard />
    <FamilyCodeCard />
    <p><a href="#parent-notifications">Choose your parent notifications and email alerts →</a></p>
    <ParentAccountsCard />
    <InviteParentCard />
    <WaitlistCard />
    <DeleteFamilyCard />
    <form className="settings-card parent-club-card" onSubmit={changePassword} style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><Gear weight="fill" /> Security</span><h2 style={{ fontSize: 20 }}>Change parent password</h2></div></div><div className="form-grid two"><label>Current password<input name="current" type="password" autoComplete="current-password" required /></label><label>New password <small>at least 8 characters</small><input name="next" type="password" minLength={8} autoComplete="new-password" required /></label></div><button className="button primary">Change password</button>{passwordSaved && <span className="saved-note"><CheckCircle weight="fill" /> Password changed</span>}</form>
    <div className="settings-card parent-club-card" style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><EnvelopeSimple weight="fill" /> Progress emails</span><h2 style={{ fontSize: 20 }}>Email sync log</h2><p>The Sunday Math Academy report auto-verifies each kid's week. IXL, DreamBox and Beast Academy all let you add a second recipient address in their own settings, so you can point them straight here without touching Gmail. {BRAND.shortName} can’t read those yet, but saves the first email as a sample for review.</p></div></div>{syncLog.length === 0 ? <p className="empty-line"><EnvelopeSimple /> No emails received yet.</p> : syncLog.map((row) => <div className="queue-row" key={String(row.id)}><div><strong>{row.subject}</strong><span>{row.sender} · {row.received_at}</span>{row.status === "sample" && <span className="sample-note">📋 {row.vendor} sample captured. Saved for review.</span>}</div><b>{row.student_name ?? ""}</b><span className={`status-dot ${row.status === "applied" ? "ok" : row.status === "sample" ? "warn" : ""}`}>{row.status}</span></div>)}</div>
  </section>;
}

// Membership (Stripe). Hidden for families that don't pay through Stripe: the founding family and
// beta families until launch.
// With startOnly, the card shows only for a family that hasn't made its first payment yet: the
// overview leads with it, because children can be added only after that payment (parental consent).
interface BillingInfo extends MembershipOfferInfo { configured: boolean; exempt: boolean; consent: boolean; status: string; billingPlan: string | null; trialEndsAt: string | null; currentPeriodEnd: string | null; canManage: boolean; access: { practice: boolean; parentEdits: boolean } }
function MembershipCard({ startOnly = false }: { startOnly?: boolean }) {
  const [info, setInfo] = useState<BillingInfo | null>(null); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { void api<BillingInfo>("/api/parent/billing").then(setInfo).catch(() => setInfo(null)); }, []);
  if (!info || info.exempt) return null;
  if (startOnly && info.consent) return null;
  async function go(path: string, body: unknown) {
    setBusy(true); setError("");
    try { window.location.assign((await api<{ url: string }>(path, { method: "POST", body: JSON.stringify(body) })).url); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Billing is unavailable right now"); setBusy(false); }
  }
  async function changePlan() {
    setBusy(true); setError("");
    try { await api("/api/parent/billing/checkout/cancel", { method: "POST", body: "{}" }); setInfo(await api<BillingInfo>("/api/parent/billing")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not close the earlier checkout"); }
    finally { setBusy(false); }
  }
  const label: Record<string, string> = { trialing: "Free trial", active: "Active", past_due: "Payment needs attention", canceled: "Ended", none: "Not started", incomplete: "Confirming your payment" };
  return <div className="settings-card parent-club-card" style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><Coins weight="fill" /> Membership</span><h2 style={{ fontSize: 20 }}>{startOnly ? "Start your membership" : label[info.status] ?? info.status}</h2><p>{startOnly ? "Add your children once your membership starts. The first payment is how we confirm a parent agreed to our privacy policy, and it’s covered by our 30-day money-back guarantee. " : ""}{info.status === "trialing" && info.trialEndsAt ? `Trial ends ${info.trialEndsAt.slice(0, 10)}. ` : ""}{info.currentPeriodEnd && info.status === "active" ? `Renews ${info.currentPeriodEnd.slice(0, 10)}. ` : ""}{startOnly ? "" : !info.access.parentEdits ? "Your family’s data is safe and readable. Restart the membership to practice and make changes." : "Payments are handled by Stripe; we never see your card."}</p></div></div>{error && <p className="form-error">{error}</p>}{info.canManage && !info.pendingCheckout && !["none", "canceled"].includes(info.status) ? <button type="button" className="small-button" disabled={busy} onClick={() => void go("/api/parent/billing/portal", {})}>Manage membership</button> : <MembershipOffer info={info} busy={busy} onCheckout={plan => void go("/api/parent/billing/checkout", { plan })} onChangePlan={() => void changePlan()}/>}</div>;
}

// Deleting a child removes every record tied to them, for good. The parent types the name to confirm.
async function deleteChild(id: string, name: string, reload: () => Promise<unknown>) {
  const typed = window.prompt(`This permanently deletes ${name} and all of ${name}'s progress, rewards and history. It can't be undone.\n\nType ${name} to confirm.`);
  if (typed === null) return;
  try { await api(`/api/parent/students/${id}/delete`, { method: "POST", body: JSON.stringify({ confirm: typed }) }); await reload(); }
  catch (caught) { window.alert(caught instanceof Error ? caught.message : "Could not delete"); }
}

// Close the family's account. Everyone is signed out now; everything is erased after 30 days.
function DeleteFamilyCard() {
  const [name, setName] = useState<string | null>(null); const [error, setError] = useState("");
  useEffect(() => { void api<{ name: string }>("/api/parent/household").then((r) => setName(r.name)).catch(() => setName(null)); }, []);
  if (!name) return null;
  async function remove(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const confirm = String(new FormData(event.currentTarget).get("confirm") ?? "");
    try { await api("/api/parent/household/delete", { method: "POST", body: JSON.stringify({ confirm }) }); window.location.assign("/"); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not delete the family"); }
  }
  return <form className="settings-card parent-club-card" style={{ marginTop: 18 }} onSubmit={remove}><div className="section-heading"><div><span className="section-kicker"><X weight="bold" /> Delete</span><h2 style={{ fontSize: 20 }}>Delete this family’s account</h2><p>Everyone is signed out right away and nothing can be seen. All of your family’s data is erased after 30 days. To keep a copy, export each child’s data first.</p></div></div><label>Type <strong>{name}</strong> to confirm<input name="confirm" autoComplete="off" required /></label>{error && <p className="form-error">{error}</p>}<button className="small-button">Delete the family account</button></form>;
}

function InviteLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return <div className="invite-link"><input readOnly value={link} aria-label="Invite link" onFocus={(e) => e.currentTarget.select()} style={{ width: "100%" }} /><button type="button" className="small-button" onClick={() => void navigator.clipboard?.writeText(link).then(() => setCopied(true))}>{copied ? "Copied" : "Copy link"}</button><small>Works once and expires. Send it yourself; we don’t email it.</small></div>;
}

// Invite another grown-up into this family (instead of setting a password for them).
function InviteParentCard() {
  const [link, setLink] = useState(""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  async function make() {
    setBusy(true); setError("");
    try { setLink((await api<{ link: string }>("/api/parent/invites", { method: "POST", body: JSON.stringify({ kind: "parent" }) })).link); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not make an invite"); }
    finally { setBusy(false); }
  }
  return <div className="settings-card parent-club-card" style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><UsersThree weight="fill" /> Grown-ups</span><h2 style={{ fontSize: 20 }}>Invite another parent</h2><p>They choose their own email and password and join this family. The link works once and expires in a week.</p></div></div>{link && <InviteLink link={link} />}{error && <p className="form-error">{error}</p>}<button type="button" className="small-button" disabled={busy} onClick={() => void make()}>Make an invite link</button></div>;
}

// The club's waitlist (shown only to the founding household, which runs the club).
function WaitlistCard() {
  const [rows, setRows] = useState<Array<{ id: string; email: string; name: string | null; created_at: string; invited_at: string | null }> | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({}); const [error, setError] = useState("");
  useEffect(() => { void api<{ signups: Array<{ id: string; email: string; name: string | null; created_at: string; invited_at: string | null }> }>("/api/parent/beta").then((r) => setRows(r.signups)).catch(() => setRows(null)); }, []);
  if (!rows) return null;
  async function invite(id: string) {
    setError("");
    try { const made = await api<{ link: string }>(`/api/parent/beta/${id}/invite`, { method: "POST", body: "{}" }); setLinks((l) => ({ ...l, [id]: made.link })); setRows((r) => r?.map((x) => x.id === id ? { ...x, invited_at: new Date().toISOString() } : x) ?? null); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not make an invite"); }
  }
  return <><div className="settings-card parent-club-card" style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><EnvelopeSimple weight="fill" /> Club</span><h2 style={{ fontSize: 20 }}>Waitlist</h2><p>Make a new-family invite link for someone on the waitlist, then send it to them yourself.</p></div></div>{error && <p className="form-error">{error}</p>}{rows.length === 0 ? <p className="empty-line">No one on the waitlist yet.</p> : rows.map((row) => <div className="queue-row" key={row.id}><div><strong>{row.email}</strong><span>{row.name ?? ""} · joined {formatShortDate(row.created_at)}{row.invited_at ? ` · invited ${row.invited_at.slice(0, 10)}` : ""}</span>{links[row.id] && <InviteLink link={links[row.id]!} />}</div><button type="button" className="small-button" onClick={() => void invite(row.id)}>{row.invited_at ? "New invite link" : "Make invite link"}</button></div>)}</div><ReviewerInviteCard /></>;
}

// A free account for a teacher or tester to look around. It skips the membership payment, so the
// reviewer adds made-up students only (the join page says so).
function ReviewerInviteCard() {
  const [note, setNote] = useState(""); const [link, setLink] = useState(""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  async function make(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(""); setLink("");
    try { setLink((await api<{ link: string }>("/api/parent/invites", { method: "POST", body: JSON.stringify({ kind: "family", reviewer: true, ...(note.trim() ? { note: note.trim() } : {}) }) })).link); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not make an invite"); }
    finally { setBusy(false); }
  }
  return <form className="settings-card parent-club-card" style={{ marginTop: 18 }} onSubmit={(event) => void make(event)}><div className="section-heading"><div><span className="section-kicker"><EnvelopeSimple weight="fill" /> Club</span><h2 style={{ fontSize: 20 }}>Invite a reviewer</h2><p>A free account for a teacher or tester. They add practice students with made-up names and can try any grade. No payment, and nothing is shared with your family.</p></div></div><label>Who it’s for <small>only you see this</small><input value={note} onChange={(event) => setNote(event.target.value)} maxLength={120} placeholder="Second grade teacher" /></label>{link && <InviteLink link={link} />}{error && <p className="form-error">{error}</p>}<button type="submit" className="small-button" disabled={busy}>Make a reviewer link</button></form>;
}

// What a child types once on a new device before picking their name. Changing it stops the old
// code working on new devices; devices that already know the family keep working.
function FamilyCodeCard() {
  const [code, setCode] = useState<string | null>(null); const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  useEffect(() => { void api<{ familyCode: string }>("/api/parent/household").then((r) => setCode(r.familyCode)).catch(() => setError("Could not load the family code")); }, []);
  async function rotate() {
    if (!window.confirm("Make a new family code? The old one will stop working on new devices. Devices already signed in keep working.")) return;
    setBusy(true); setError("");
    try { setCode((await api<{ familyCode: string }>("/api/parent/household/code", { method: "POST", body: "{}" })).familyCode); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not make a new code"); }
    finally { setBusy(false); }
  }
  return <div className="settings-card parent-club-card" style={{ marginTop: 18 }}><div className="section-heading"><div><span className="section-kicker"><ShieldCheck weight="fill" /> Kids’ sign-in</span><h2 style={{ fontSize: 20 }}>Family code</h2><p>Your kids type this once on a new device, then pick their name and enter their PIN. Their names never show on a device that hasn’t been given the code.</p></div></div>{code && <p className="family-code" aria-label="Family code"><strong style={{ fontSize: 28, letterSpacing: "0.15em", fontFamily: "ui-monospace, monospace" }}>{code}</strong></p>}{error && <p className="form-error">{error}</p>}<button type="button" className="small-button" disabled={busy || !code} onClick={() => void rotate()}>Make a new code</button></div>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) { const ref = useDialog<HTMLElement>(onClose); return <div className="modal-backdrop parent-club-overlay" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}><section ref={ref} tabIndex={-1} className="modal parent-club-dialog" role="dialog" aria-modal="true" aria-label={title}><header><div><span className="eyebrow">Parent dashboard</span><h2>{title}</h2></div><button type="button" className="icon-button" aria-label={`Close ${title}`} onClick={onClose}><X /></button></header>{children}</section></div>; }
function EmptyState({ icon, title, body, action, onAction }: { icon: ReactNode; title: string; body: string; action?: string; onAction?: () => void }) { return <div className="empty-state"><span>{icon}</span><h3>{title}</h3><p>{body}</p>{action && onAction && <button className="button primary" onClick={onAction}>{action}</button>}</div>; }
function initials(name: string) { return name.split(/\s+/u).slice(0, 2).map((part) => part[0]?.toUpperCase()).join(""); }
function tabTitle(tab: Tab) { return ({ curriculum: "Course browser", review: "Teacher review", math: "Learning and progress", overview: "Family overview", verify: "Review activity", crew: "Student settings", library: "Prize library",
  notifications: "Notifications", guide: "Start here", settings: "Family settings" } as const)[tab]; }
function formatDate(date: string) { return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
// Accepts a day ("2026-10-15") or a stored timestamp ("2026-10-15 09:30:00"); shows "Oct 15".
function formatShortDate(date: string) { const day = String(date).slice(0, 10); const value = new Date(`${day}T12:00:00`); return Number.isNaN(value.getTime()) ? day : value.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(day.slice(0, 4) === String(new Date().getFullYear()) ? {} : { year: "numeric" }) }); }


// Household logins. Individual accounts replaced the single shared password
// after it did the predictable thing and locked everyone out at once.
function ParentAccountsCard() {
  const [rows, setRows] = useState<Array<Record<string, string | number | null>>>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await api<{ parents: Array<Record<string, string | number | null>>; currentParentId: string | null }>("/api/parent/parents");
    setRows(data.parents);
    setCurrentId(data.currentParentId);
  }
  useEffect(() => { void load().catch(e=>setError(e instanceof Error?e.message:"Could not load your family dashboard")); }, []);

  async function addParent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(""); setSaved("");
    try {
      await api("/api/parent/parents", {
        method: "POST",
        body: JSON.stringify({ name: form.get("name"), email: form.get("email"), password: form.get("password") }),
      });
      (event.target as HTMLFormElement).reset();
      setSaved("Account added");
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add that account");
    } finally { setBusy(false); }
  }

  // No mail provider needed for the case that actually happens: one person
  // forgets, the other sets them a new one and says it out loud.
  async function resetOther(id: string, name: string) {
    const next = prompt(`Set a new password for ${name}. Tell them what it is; they can change it once they're in.`);
    if (!next) return;
    if (next.length < 8) { setError("Passwords need at least 8 characters"); return; }
    setError("");
    try {
      await api(`/api/parent/parents/${id}/password`, { method: "POST", body: JSON.stringify({ password: next }) });
      setSaved(`${name}'s password is set`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not set that password");
    }
  }

  async function deactivate(id: string, name: string) {
    if (!confirm(`Remove ${name}'s login? Their history stays; they just can't sign in.`)) return;
    setError("");
    try {
      await api(`/api/parent/parents/${id}/deactivate`, { method: "POST", body: "{}" });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not remove that account");
    }
  }

  const active = rows.filter((row) => Number(row.active) === 1);

  return (
    <section className="settings-card parent-club-card" style={{ marginTop: 18 }}>
      <div className="section-heading"><div>
        <span className="section-kicker"><UsersThree weight="fill" /> Household</span>
        <h2 style={{ fontSize: 20 }}>Parent logins</h2>
        <p>Each grown-up gets their own email and password, so changing yours never locks the other one out. Both have the same access to every kid, and either can set the other a new password if they forget, with no reset email involved.</p>
      </div></div>

      {active.length === 0 && (
        <div className="policy-note"><ShieldCheck weight="fill" /><div>
          <strong>Still on the shared password.</strong>
          <span>Add the first account below. The shared password keeps working until you do, then it retires.</span>
        </div></div>
      )}

      {active.length > 0 && (
        <ul className="parent-list">
          {active.map((row) => (
            <li key={String(row.id)}>
              <div>
                <strong>{String(row.name)}{String(row.id) === currentId && <span className="you-chip">you</span>}</strong>
                <small>{String(row.email)}</small>
                <small>{row.last_login_at ? `Last signed in ${formatShortDate(String(row.last_login_at))}` : "Never signed in"}</small>
              </div>
              {String(row.id) !== currentId && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="small-button" onClick={() => void resetOther(String(row.id), String(row.name))}>Set password</button>
                  <button className="small-button" onClick={() => void deactivate(String(row.id), String(row.name))}>Remove</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={addParent} style={{ marginTop: 14 }}>
        <div className="form-grid three">
          <label>Name<input name="name" maxLength={80} required /></label>
          <label>Email<input name="email" type="email" maxLength={200} required /></label>
          <label>Password <small>at least 8</small><input name="password" type="password" minLength={8} autoComplete="new-password" required /></label>
        </div>
        <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>
          Use an email address this parent can access for account messages and password recovery.
        </p>
        {error && <p className="form-error">{error}</p>}
        <button className="button primary" disabled={busy} style={{ marginTop: 12 }}>{busy ? "Adding…" : "Add parent login"}</button>
        {saved && <span className="saved-note"><CheckCircle weight="fill" /> {saved}</span>}
      </form>
    </section>
  );
}


// Photograph the weekly report instead of wiring up email forwarding. Works for
// any app that shows the numbers on a screen, which is all of them, and needs
// nothing from the family beyond a phone.
function ScanCard({ students, onApplied, onError }: {
  students: Array<{ id: string; name: string }>;
  onApplied: () => Promise<void> | void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState<null | {
    studentName: string | null; matchedStudentId: string | null; weekStart: string | null;
    amounts: number[]; engine: string; notes: string | null; complete: boolean;
  }>(null);
  const [studentId, setStudentId] = useState("");
  const [weekStart, setWeekStart] = useState("");
  const [amounts, setAmounts] = useState<number[]>([]);
  const [saved, setSaved] = useState("");

  async function pick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 6_000_000) { onError("That image is over 6 MB. A screenshot is usually well under."); return; }
    setBusy(true); setSaved(""); setScan(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(new Error("Could not read that file"));
        reader.readAsDataURL(file);
      });
      const mediaType = file.type === "image/jpeg" || file.type === "image/webp" ? file.type : "image/png";
      const result = await api<typeof scan & object>("/api/parent/scan", {
        method: "POST", body: JSON.stringify({ image: base64, mediaType }),
      });
      setScan(result);
      setStudentId(result.matchedStudentId ?? students[0]?.id ?? "");
      setWeekStart(result.weekStart ?? "");
      setAmounts(result.amounts.length === 7 ? result.amounts : [0, 0, 0, 0, 0, 0, 0]);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not read that image");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  async function apply() {
    setBusy(true);
    try {
      await api("/api/parent/scan/apply", {
        method: "POST", body: JSON.stringify({ studentId, weekStart, amounts }),
      });
      setSaved("Week applied");
      setScan(null);
      await onApplied();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not apply that week");
    } finally { setBusy(false); }
  }

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <section className="settings-card scan-card">
      <div className="section-heading"><div>
        <span className="section-kicker"><Sparkle weight="fill" /> Scan a report</span>
        <h2 style={{ fontSize: 20 }}>Photograph the weekly report</h2>
        <p>Upload a screenshot of the weekly progress report from Math Academy, Beast Academy or XtraMath. Check the numbers, then apply. Nothing is written until you do.</p>
      </div></div>

      {!scan && (
        <label className="scan-drop">
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} disabled={busy} />
          <span>{busy ? "Reading…" : "Choose a screenshot"}</span>
        </label>
      )}
      {saved && <span className="saved-note"><CheckCircle weight="fill" /> {saved}</span>}

      {scan && (
        <div className="scan-review">
          {!scan.complete && <p className="form-error">{scan.engine === "none" ? "Could not read the image automatically. Fill the week in below." : "Only part of the report came through. Fix anything wrong before applying."}</p>}
          {scan.notes && <p className="muted" style={{ fontSize: 13 }}>Reader note: {scan.notes}</p>}
          <div className="form-grid two">
            <label>Student
              <select value={studentId} onChange={(event) => setStudentId(event.target.value)}>
                {students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
              </select>
            </label>
            <label>Week starting (Monday)
              <input type="date" value={weekStart} onChange={(event) => setWeekStart(event.target.value)} />
            </label>
          </div>
          <div className="scan-days">
            {days.map((day, index) => (
              <label key={day}>{day}
                <input
                  type="number"
                  value={Number.isFinite(amounts[index]) ? amounts[index] : 0}
                  onChange={(event) => {
                    const next = [...amounts];
                    next[index] = parseInt(event.target.value, 10) || 0;
                    setAmounts(next);
                  }}
                />
              </label>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 12.5 }}>
            Negative days are fine and are read as written. They credit zero and never reduce what was already earned.{scan.engine !== "none" && ` Read by ${scan.engine}.`}
          </p>
          <div className="modal-actions">
            <button className="button ghost" onClick={() => setScan(null)} disabled={busy}>Cancel</button>
            <button className="button primary" onClick={() => void apply()} disabled={busy || !studentId || !weekStart}>
              {busy ? "Applying…" : "Apply this week"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


// Prizes a kid has paid for and not yet received. This is the list that keeps
// the currency honest: an approved prize that never arrives teaches a child
// that the whole economy is pretend, and it is the single most common way a
// family reward system quietly dies.
function OwedPanel({ owed, onFulfil }: { owed: OwedPrize[]; onFulfil: (id: string) => Promise<void> }) {
  return (
    <section className="owed-panel">
      <div className="section-heading"><div>
        <span className="section-kicker"><Gift weight="fill" /> Still owed</span>
        <h2 style={{ fontSize: 20 }}>{owed.length} prize{owed.length === 1 ? "" : "s"} to hand over</h2>
        <p>Approval keeps the bucks in their balance. Mark delivered after handing it over to deduct the price. Previously charged approvals will not charge twice.</p>
      </div></div>
      <ul>
        {owed.map((prize) => (
          <li key={prize.id}>
            <div>
              <strong>{prize.student_name} · {prize.prize_name_snapshot}</strong>
              <small>{prize.cost_snapshot} price{prize.resolved_at ? ` · approved ${String(prize.resolved_at).slice(0, 10)}` : ""}</small>
            </div>
            <button className="button success" onClick={() => void onFulfil(prize.id)}>Delivered</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Every stamp the family has minted, split by child, with a way into each
// child's full trail. Reached by clicking the circulation figure on the
// overview, because a total that cannot be opened is a number you are being
// asked to trust.
function Circulation({ students, currency, onClose, onOpenLedger }: {
  students: Array<{ id: string; name: string; balances: { balance: number; pending: number; spendable: number; vaulted: number } }>;
  currency: string;
  onClose: () => void;
  onOpenLedger: (id: string, name: string) => void;
}) {
  const total = students.reduce((sum, s) => sum + s.balances.balance, 0);
  const pending = students.reduce((sum, s) => sum + s.balances.pending, 0);
  const spendable = students.reduce((sum, s) => sum + s.balances.spendable, 0);
  const vaulted = students.reduce((sum, s) => sum + s.balances.vaulted, 0);

  return (
    <Modal title={`${currency} in circulation`} onClose={onClose}>
      <div className="audit">
        <div className="audit-totals">
          <div><span>Ready to spend</span><strong>{spendable.toLocaleString()}</strong></div>
          <div className={pending > 0 ? "warn" : ""}><span>Waiting on you</span><strong>{pending.toLocaleString()}</strong></div>
          <div><span>In vaults</span><strong>{vaulted.toLocaleString()}</strong></div>
          <div><span>All of it</span><strong>{total.toLocaleString()}</strong></div>
        </div>
        <p className="audit-reconcile">
          Everything the family has ever minted, minus everything spent. Open a
          child to see each line and where it came from.
        </p>
        <ul className="audit-list">
          {students.length === 0 && <li className="audit-empty">No students yet.</li>}
          {students.map((student) => (
            <li key={student.id} className={student.balances.pending > 0 ? "pending" : ""}>
              <span className="audit-delta">{student.balances.balance.toLocaleString()}</span>
              <span className="audit-what">
                <strong>{student.name}</strong>
                <small>
                  {student.balances.spendable.toLocaleString()} ready
                  {student.balances.pending > 0 && ` · ${student.balances.pending.toLocaleString()} waiting on you`}
                  {student.balances.vaulted > 0 && ` · ${student.balances.vaulted.toLocaleString()} vaulted`}
                </small>
              </span>
              <button className="small-button" onClick={() => onOpenLedger(student.id, student.name)}>
                Ledger
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// ---- The audit trail ----
//
// Every movement of a child's currency, most recent first, with what it was for
// resolved into words. Built because a balance nobody can explain is a balance
// nobody should trust: this app had exactly that bug, where a data migration
// credited currency that had not been verified yet and there was no screen
// anywhere that would have shown it.
//
// Two things this deliberately does. It shows PENDING rows plainly, because
// currency attached to a day no parent has checked is not the child's to spend
// yet and that distinction is invisible in a plain running total. And it never
// offers a "delete" — the ledger is append-only in the database, corrections
// are new rows that reverse old ones, and both stay on the record.
interface LedgerRow {
  id: string;
  delta: number;
  reason: string;
  ref_id: string | null;
  note: string | null;
  created_at: string;
  entry_date: string | null;
  entry_amount: number | null;
  entry_status: string | null;
  prize_name: string | null;
  prize_status: string | null;
  entry_source: string | null;
  verified_by: string | null;
  verified_at: string | null;
  proof_sender: string | null;
  proof_subject: string | null;
  proof_received: string | null;
  pending: number;
}

// Where a line came from, in the words a parent would use if asked to justify
// it. This is the "proof" half of an audit trail: a number with no provenance
// is a number you have to take on faith, and the whole point of this screen is
// that nobody has to.
function provenance(row: LedgerRow): string | null {
  if (row.proof_sender) {
    return `Read from ${row.proof_subject ?? "a weekly report"} · sent by ${row.proof_sender}`;
  }
  if (row.entry_source === "email_sync") return "Came in from the weekly report email";
  if (row.entry_status === "unverified") return "Typed in by hand · not checked off yet";
  if (row.entry_status === "verified") {
    const who = row.verified_by ? ` by ${row.verified_by}` : "";
    const when = row.verified_at ? ` on ${formatShortDate(row.verified_at)}` : "";
    return `Typed in by hand · checked${who}${when}`;
  }
  return null;
}

const REASON_LABEL: Record<string, string> = {
  earn: "Earned from practice",
  surplus: "Extra work past the target",
  streak: "7-day streak checkpoint",
  mastery: "Mastery bonus",
  redeem: "Prize redeemed",
  adjust: "Correction",
};

export function ledgerLabel(row:{reason:string;ref_id:string|null}) {
 const kind=row.ref_id?.startsWith('engagement:')?row.ref_id.split(':')[1]:null;
 const labels:Record<string,string>={dailyGoal:'Daily goal',streakMilestone:'7-day streak milestone',review:'Review passed',chapter:'Chapter finished',unit:'Unit mastered',mastery:'Topic mastered',parentBonus:'Parent bonus',gear:'Optional gear unlock'};
 if(kind&&labels[kind])return labels[kind];
 if(row.ref_id?.endsWith(':delivery-undo'))return 'Delivery undone';
 return REASON_LABEL[row.reason]??row.reason;
}

function LedgerAudit({ studentId, name, currency, onClose, onCorrect }: {
  studentId: string; name: string; currency: string; onClose: () => void; onCorrect: () => void;
}) {
  const [data, setData] = useState<{
    balances: { balance: number; pending: number; spendable: number; vaulted: number };
    totals: { earned: number; bonuses: number; spent: number; corrections: number };
    ledger: LedgerRow[];
  } | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void api<NonNullable<typeof data>>(`/api/parent/students/${studentId}/ledger`)
      .then(setData)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Could not load the ledger"));
  }, [studentId]);

  return (
    <Modal title={`${name}'s ledger`} onClose={onClose}>
      {error && <p className="form-error">{error}</p>}
      {!data ? <p className="audit-empty">Loading…</p> : (
        <div className="audit">
          <div className="audit-totals">
            <div><span>Ready to spend</span><strong>{data.balances.spendable.toLocaleString()}</strong></div>
            <div className={data.balances.pending > 0 ? "warn" : ""}>
              <span>Waiting on you</span><strong>{data.balances.pending.toLocaleString()}</strong>
            </div>
            <div><span>In the vault</span><strong>{data.balances.vaulted.toLocaleString()}</strong></div>
            <div><span>Balance</span><strong>{data.balances.balance.toLocaleString()}</strong></div>
          </div>
          <p className="audit-reconcile">
            {data.totals.earned.toLocaleString()} earned · {data.totals.bonuses.toLocaleString()} in bonuses
            · {Math.abs(data.totals.spent).toLocaleString()} spent
            {data.totals.corrections !== 0 && ` · ${data.totals.corrections > 0 ? "+" : ""}${data.totals.corrections.toLocaleString()} in corrections`}
          </p>
          {data.balances.pending > 0 && (
            <p className="audit-note">
              {data.balances.pending.toLocaleString()} {currency.toLowerCase()} is attached to work you
              have not verified yet. {name} can see it, but cannot spend it until you check the day off.
            </p>
          )}

          <ul className="audit-list">
            {data.ledger.length === 0 && <li className="audit-empty">Nothing has moved yet.</li>}
            {data.ledger.map((row) => (
              <li key={row.id} className={row.pending ? "pending" : ""}>
                <span className={`audit-delta${row.delta < 0 ? " down" : ""}`}>
                  {row.delta > 0 ? "+" : ""}{row.delta.toLocaleString()}
                </span>
                <span className="audit-what">
                  <strong>{ledgerLabel(row)}</strong>
                  <small>
                    {row.prize_name
                      ? `${row.prize_name}${row.prize_status ? ` · ${row.prize_status}` : ""}`
                      : row.entry_date
                        ? `${formatShortDate(row.entry_date)} · ${row.entry_amount ?? 0} logged`
                        : row.note ?? "—"}
                    {row.entry_date && row.note ? ` · ${row.note}` : ""}
                  </small>
                  {provenance(row) && <small className="audit-proof">{provenance(row)}</small>}
                </span>
                <span className="audit-when">
                  {row.pending ? <em>waiting</em> : row.created_at.slice(0, 10)}
                </span>
              </li>
            ))}
          </ul>

          <div className="audit-actions">
            <button className="small-button" onClick={onCorrect}>Post a correction</button>
            <small>Corrections add a new line. Nothing is ever deleted, and currency is never taken as punishment.</small>
          </div>
        </div>
      )}
    </Modal>
  );
}
